import { useState } from 'react'
import { Alert, Button, CircularProgress, Container, Paper, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import {
    CONTACT_VERIFICATION_EMAIL_KEY,
    CONTACT_VERIFICATION_EMAIL_VALIDATED_KEY,
    CONTACT_VERIFICATION_PHONE_VALIDATED_KEY,
    setItemToLS,
    useRequestContactVerificationEmailToken,
    useValidateContactVerificationEmailToken,
} from '../../core/api'

export default function VerificationEmailCard() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { verificationToken = '' } = useParams()
    const requestEmailToken = useRequestContactVerificationEmailToken()
    const validateEmailToken = useValidateContactVerificationEmailToken()
    const [email, setEmail] = useState('')
    const [otp, setOtp] = useState('')
    const [error, setError] = useState('')
    const [emailTokenRequested, setEmailTokenRequested] = useState(false)
    const [emailValidated, setEmailValidated] = useState(false)

    function handleUseOtherEmailAddress() {
        setEmail('')
        setOtp('')
        setError('')
        setEmailTokenRequested(false)
    }

    async function handleRequestEmailToken(event) {
        event.preventDefault()
        setError('')

        try {
            await requestEmailToken.mutateAsync({ email: email.trim() })
            setItemToLS(CONTACT_VERIFICATION_EMAIL_KEY, email.trim())
            setEmailTokenRequested(true)
        } catch (err) {
            setError(err?.response?.data?.message || err.message || t('verification.emailRequestError'))
        }
    }

    async function handleValidateEmailToken(event) {
        event.preventDefault()
        setError('')

        try {
            const result = await validateEmailToken.mutateAsync({ token: otp.trim() })

            if (!result?.valid) {
                setError(t('verification.emailOtpInvalid'))
                return
            }

            setItemToLS(CONTACT_VERIFICATION_EMAIL_VALIDATED_KEY, true)
            setItemToLS(CONTACT_VERIFICATION_PHONE_VALIDATED_KEY, false)
            setEmailValidated(true)
            navigate(`/verify/${verificationToken}/phone`)
        } catch (err) {
            setError(err?.response?.data?.message || err.message || t('verification.emailValidateError'))
        }
    }

    return (
        <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
            <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                    <div>
                        <Typography variant="h3" gutterBottom>
                            {t('verification.emailTitle')}
                        </Typography>
                        <Typography color="text.secondary">{t('verification.emailDescription')}</Typography>
                    </div>
                    {emailTokenRequested && !emailValidated && (<Alert severity="info">{t('verification.emailTokenRequested')}</Alert>)}
                    {!emailValidated && error ? <Alert severity="error">{error}</Alert> : null}
                    {emailValidated ? (
                        <Alert severity="success">{t('verification.emailValidated')}</Alert>
                    ) : (
                        <>
                            <Stack component="form" spacing={2} onSubmit={handleRequestEmailToken}>
                                <TextField
                                    autoComplete="email"
                                    fullWidth
                                    label={t('verification.emailLabel')}
                                    name="email"
                                    onChange={(event) => setEmail(event.target.value)}
                                    required
                                    type="email"
                                    value={email}
                                    disabled={requestEmailToken.isPending || emailTokenRequested}
                                />
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        fullWidth
                                        disabled={requestEmailToken.isPending}
                                        loading={requestEmailToken.isPending}
                                        type="submit"
                                        variant="contained"
                                        color={emailTokenRequested ? "inherit" : "primary"}
                                    >
                                        {emailTokenRequested ? t('verification.resendEmailToken') : t('verification.requestEmailToken')}
                                    </Button>
                                    {emailTokenRequested && (
                                        <Button
                                            fullWidth
                                            disabled={requestEmailToken.isPending}
                                            loading={requestEmailToken.isPending}
                                            onClick={handleUseOtherEmailAddress}
                                            variant="text"
                                        >
                                            {t('verification.useOtherEmailAddress')}
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                            {emailTokenRequested ? (
                                <Stack component="form" spacing={2} onSubmit={handleValidateEmailToken}>
                                    <TextField
                                        autoComplete="one-time-code"
                                        fullWidth
                                        label={t('verification.emailOtpLabel')}
                                        name="otp"
                                        onChange={(event) => setOtp(event.target.value)}
                                        required
                                        value={otp}
                                    />
                                    <Button loading={validateEmailToken.isPending} type="submit" variant="contained" color="primary">
                                        {validateEmailToken.isPending ? <CircularProgress color="inherit" size={20} /> : t('verification.validateEmailToken')}
                                    </Button>
                                </Stack>
                            ) : null}
                        </>
                    )}
                </Stack>
            </Paper>
        </Container>
    )
}
